import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
  type SetupStore,
} from "#src/stores/state-wrapper.ts";

import { useInstanceSettingsStore } from "#src/stores/instance-settings.ts";
import { usePrimaryInsuredStore, INSURED_TYPES } from "#src/stores/insured.ts";
import { useEappStore } from "#src/stores/electronic-application.ts";

import { getProduct, type Product } from "#src/types/products/Product.ts";
import {
  type ProductType,
  extractProducts,
  PRODUCT_TYPE,
} from "#src/constants/products.ts";
import { type ConceptType, CONCEPT_TYPE } from "#src/constants/concepts.ts";

import { initSocketForIllustration } from "#src/services/socket.service.ts";
import { InitializationsService } from "#src/services/initializations.service.js";
import { getIllustration } from "#src/services/http-illustrations.service.ts";
import { validatePartyData } from "#src/types/products/QuoteParameterValidation.ts";
import { type StoreGeneric, type Pinia } from "pinia";
import {
  type QuoteRequestBody,
  type QuoteRequestParams,
  type QuoteParams,
} from "#src/types/QuoteParams.ts";
import { computed, type ComputedRef } from "vue";
import { type Quote, type ExcludedCarrier } from "#src/types/Quote.ts";
import { fetchQuotes } from "#src/services/http-quotes.service.ts";

interface QuotingState {
  params: QuoteParams;
  rollbackPoint: QuoteParams;
  useDefaultsOnCompare: boolean;
  requestPreferences: {
    carriers: number[] | string[];
    products: number[] | string[];
    excluded_carriers: ExcludedCarrier[];
  };
}
interface Quoting extends SetupStore, ToSetupState<QuotingState> {
  applicationState: ComputedRef<string | null>;
  selectedQuoteType: ComputedRef<ProductType | ConceptType | null>;
  selectedProductType: ComputedRef<Product | null>;
  baseQuoteParams: ComputedRef<Partial<QuoteRequestParams>>;
  partnerDiscountVerboseInfoIsValid(): boolean;
  partnerDiscountBasicInfoIsValid(): boolean;
  setRollbackPoint(): void;
  getQuotes(arg: {
    params: QuoteRequestParams;
    websocket_uuid: string;
    pdf?: boolean;
    summary?: boolean;
    scenarios?: QuoteRequestParams[];
  }): any; //NOTE(tim): Properly type
  saveExcludedCarriers(): void;
  getQuoteIllustration(): Promise<string | null>;
  handleProductChange(): void;
  initializeQuoteParams(payload: Partial<QuoteParams>): void;
  rollbackQuoteParams(): void;
  storeQuoteParametersFromEApp(payload: any, primaryQuote?: Quote): void;
  setLocationByIp(): Promise<void>;
}

function applyPatch<T extends object>(target: T, patch: Partial<T>): T {
  for (const [k, v] of Object.entries(target) as [keyof T, T[keyof T]][]) {
    if (patch[k] !== undefined) {
      target[k] = patch[k]; // type-safe: k is keyof T and v is T[keyof T]
    }
  }
  return target;
}

function initializeParams(): QuoteParams {
  return {
    age_value: null,
    benefit_period: null,
    cash_value: null,
    crediting_rate: null,
    discount: null,
    elimination_period: null,
    face_amount: null,
    monthly_benefit: null,
    partner_discount: null,
    joint_income: null,
    filter_chronic_illness_and_ltc: null,
    guaranteed_issue: null,
    home_health_care_waiver: null,
    income_amount: null,
    income_end_age: null,
    income_solve: null,
    income_start_age: null,
    inflation_percentage: null,
    lapse_protection_to_age: null,
    loan_type: null,
    living_benefits: null,
    ltc_rider_percentage: null,
    mode: null,
    pool_of_money: null,
    pay_duration: null,
    premium: null,
    return_of_premium_rider: null,
    section_1035_exchange_amount: null,
    save_age: null,
    selected_type: null,
    subtype: null, // not stored in db
    shared_care: null,
    joint_waiver_of_premium: null,
    solve: null,
    state: null,
    active_years: null,
    vitality_level_filter: null,
    use_pool_of_money: null,
  };
}

function initialState(): QuotingState {
  return {
    params: initializeParams(),
    rollbackPoint: initializeParams(),
    useDefaultsOnCompare: true,
    requestPreferences: { carriers: [], products: [], excluded_carriers: [] },
  };
}

function initializeQuoting(pinia: Pinia): Quoting {
  const state = generateInitialState<QuotingState>(initialState());
  function $reset() {
    resetState<QuotingState>(state, initialState());
  }

  const applicationState = computed(() => state.params.value.state || null);
  const selectedQuoteType = computed(() => {
    if (state.rollbackPoint.value?.selected_type) {
      return state.rollbackPoint.value.selected_type;
    }

    const eapp = useEappStore(pinia);
    const products = extractProducts(
      eapp.selectedQuote?.product?.category || ""
    );
    if (products?.length) return products[0];
    return null;
  });
  const selectedProductType = computed(() => {
    if (!selectedQuoteType.value) return null;
    return getProduct(selectedQuoteType.value);
  });

  // TODO: drop this
  const baseQuoteParams: Quoting["baseQuoteParams"] = computed(() => {
    const insured = usePrimaryInsuredStore(pinia);
    const instanceSettings = useInstanceSettingsStore(pinia);

    const data: Partial<QuoteRequestParams> = {
      mode: state.params.value.mode,
      state: state.params.value.state,
      birthdate: insured.birthdate.model?.toString(),
      gender: insured.gender.model?.toString(),
      first_name: insured.first_name.model?.toString(),
      last_name: insured.last_name.model?.toString(),
      height: +(insured.height.model || 0),
      weight: +(insured.weight.model || 0) || null,
      smoker: insured.smokerForRequests.smoker?.toString(),
      health: +(insured.health.model || 0),
      usages: insured.smokerForRequests.usages,
      agent: { id: instanceSettings.agent.id },
    };

    return data;
  });

  function partnerDiscountVerboseInfoIsValid() {
    const items = validatePartyData(pinia, INSURED_TYPES.JOINT, {
      validationItems: [
        "first_name",
        "last_name",
        "health",
        "birthdate",
        "gender",
        "smoker",
        "smoking_statuses",
        "marital_status",
      ],
      errorKey: INSURED_TYPES.JOINT,
    });
    return Object.values(items).every(v => v.validation.length === 0);
  }
  function partnerDiscountBasicInfoIsValid() {
    const items = validatePartyData(pinia, INSURED_TYPES.JOINT, {
      validationItems: ["first_name", "last_name", "marital_status"],
      errorKey: INSURED_TYPES.JOINT,
    });
    return Object.values(items).every(v => v.validation.length === 0);
  }
  function setRollbackPoint() {
    state.rollbackPoint.value = JSON.parse(
      JSON.stringify({ ...state.params.value })
    );
  }

  function getQuotes({
    params: quoteParams,
    websocket_uuid,
    pdf = false,
    summary = undefined,
    scenarios = undefined,
  }: Parameters<Quoting["getQuotes"]>[0]) {
    const instance = useInstanceSettingsStore(pinia);
    const body: QuoteRequestBody = {
      quote: quoteParams,
      summary,
      websocket_uuid,
      scenarios,
      deferred: true,
      domain_key: instance.domain_key,
    };

    if (state.requestPreferences.value.carriers.length) {
      body.quote.carriers = state.requestPreferences.value.carriers;
    }

    if (state.requestPreferences.value.products.length) {
      body.quote.products = state.requestPreferences.value.products;
    }

    if (state.requestPreferences.value.excluded_carriers.length) {
      body.quote.excluded_carrier_ids =
        state.requestPreferences.value.excluded_carriers.map(c => c.id);
    }

    if (scenarios) body.scenarios = scenarios;

    return fetchQuotes(pinia, { pdf, body });
  }
  async function saveExcludedCarriers() {
    const eapp = useEappStore(pinia);
    eapp.updateEapp({
      excluded_carrier_ids:
        state.requestPreferences.value.excluded_carriers.map(i => i.id),
    });
  }
  async function getQuoteIllustration() {
    // TODO: FIX THIS
    //eslint-disable-next-line
    return new Promise<string>(async (resolve, reject) => {
      let pusher: any; //NOTE(tim): Propertly Type
      const pusherDisconnect = () => {
        if (pusher.disconnect) pusher.disconnect();
      };
      const failHandler = pusherDisconnect;

      const eApp = useEappStore(pinia);

      const quoteId = eApp.selectedQuote.real_id;
      const eAppId = eApp.id;
      const eAppUuid = eApp.uuid;

      if (!eAppId || !eAppUuid) throw "Missing id and uuid";
      if (!quoteId) throw "Missing Quote Id";

      const endHandler = async () => {
        pusherDisconnect();
        const settingStore = useInstanceSettingsStore(pinia);
        const searchParams = new URLSearchParams();
        searchParams.append("electronic_application_id", eAppId.toString());
        searchParams.append("uuid", eAppUuid);
        resolve(
          `${settingStore.api_url}/strife/v1/illustrations/${quoteId}?${searchParams}`
        );
      };

      const fetchIllustrationHandler = async () => {
        try {
          const { status } = await getIllustration(pinia, quoteId);
          if (status === 200) {
            pusherDisconnect();
            endHandler();
          }
        } catch (e) {
          pusherDisconnect();
          reject(e);
        }
      };
      try {
        pusher = await initSocketForIllustration(eAppUuid, {
          fetchIllustrationHandler,
          failHandler,
          endHandler,
        });
      } catch (e) {
        reject(e);
      }
    });
  }
  function handleProductChange() {
    if (!state.params.value.selected_type) return;
    const productType = getProduct(state.params.value.selected_type);
    const insuredStore = usePrimaryInsuredStore(pinia);
    const productConverter = productType.eAppParser({
      ...state.params.value,
      age: +insuredStore.age,
    });
    applyPatch(state.params.value, productConverter);
  }

  function initializeQuoteParams(payload: Partial<QuoteParams>) {
    applyPatch(state.params.value, payload);
  }

  function rollbackQuoteParams() {
    state.params.value = JSON.parse(JSON.stringify(state.rollbackPoint.value));
  }

  function storeQuoteParametersFromEApp(payload: any, primaryQuote?: Quote) {
    let productParsedValues: Partial<QuoteParams> = {};
    state.useDefaultsOnCompare.value = false;

    let quoteParams = {
      ...payload,
    };

    if (primaryQuote?.id) quoteParams = { ...quoteParams, ...primaryQuote };

    const { selected_type, ...restOfParams } = quoteParams;

    if (selected_type) {
      const ulSubtypes = [
        PRODUCT_TYPE.ACCUMULATION_IUL,
        PRODUCT_TYPE.PROTECTION_IUL,
        PRODUCT_TYPE.PROTECTION_VUL,
        PRODUCT_TYPE.UNIVERSAL_LIFE,
      ];

      if (ulSubtypes.includes(selected_type)) {
        quoteParams.selected_type = CONCEPT_TYPE.ALL_UL;
        quoteParams.subtype = selected_type;
      } else {
        quoteParams.selected_type = selected_type;
      }

      const productType = getProduct(selected_type);
      const insured = usePrimaryInsuredStore(pinia);

      if (productType?.eAppParser) {
        productParsedValues = productType.eAppParser({
          ...restOfParams,
          age: insured.age,
        });
        if ("selected_type" in productParsedValues) {
          delete productParsedValues.selected_type;
        }
      }
    }

    applyPatch(state.params.value, {
      ...quoteParams,
      ...productParsedValues,
    });

    if (state.params.value.benefit_period === null) {
      state.params.value.benefit_period = 4;
    }

    state.requestPreferences.value.excluded_carriers =
      primaryQuote?.excluded_carriers || [];

    setRollbackPoint();
  }
  async function setLocationByIp() {
    let quoteState, ipState;
    const instanceSettings = useInstanceSettingsStore(pinia);
    try {
      ipState = await new InitializationsService(pinia).fetchStateByIp();
      const value =
        state.params.value.state || instanceSettings.override_state || ipState;

      ipState = instanceSettings.agent.insurance_licenses.find(
        license => license.state === value
      )?.state;
      if (ipState) quoteState = ipState;
      // eslint-disable-next-line no-unused-vars
    } catch (e) {
      //do nothing
    }
    if (!quoteState) {
      const instanceSettings = useInstanceSettingsStore(pinia);
      const agent = instanceSettings.agent;
      if (agent.insurance_licenses?.length) {
        let license = agent.insurance_licenses.find(
          license => license.resident
        );
        if (!license) license = agent.insurance_licenses[0];
        quoteState = license?.state;
      }
    }

    state.params.value.state = quoteState || ipState; // use IP as a fallback
  }

  return {
    $reset,
    ...state,
    applicationState,
    selectedQuoteType,
    selectedProductType,
    baseQuoteParams,
    partnerDiscountVerboseInfoIsValid,
    partnerDiscountBasicInfoIsValid,
    setRollbackPoint,
    getQuotes,
    saveExcludedCarriers,
    getQuoteIllustration,
    handleProductChange,
    initializeQuoteParams,
    rollbackQuoteParams,
    storeQuoteParametersFromEApp,
    setLocationByIp,
  };
}

export function useQuotingStore(pinia: Pinia, hot?: StoreGeneric) {
  return defineStore<string, Quoting>("quoting", () => {
    return initializeQuoting(pinia);
  })(pinia, hot);
}
